export const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
export const APP = process.env.REACT_APP_APP_URL || 'https://app.spinboard.app';

// ── The shop's hours as one line (3 Nov) ────────────────────────────────────
// The Store Hours text if the shop wrote one, otherwise written from the
// day-by-day hours in Settings › Shop. Mirrors server/helpers/hours.js.
const DAY3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const time12 = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ''));
  if (!m) return '';
  const h = Number(m[1]);
  return `${h % 12 || 12}${m[2] === '00' ? '' : `:${m[2]}`}${h >= 12 ? 'pm' : 'am'}`;
};
export function hoursSummary(s) {
  if (!Array.isArray(s) || s.length !== 7) return '';
  const ok = s.every((d) => d && (d.closed || (/^\d{1,2}:\d{2}$/.test(d.open || '') && /^\d{1,2}:\d{2}$/.test(d.close || '') && d.close > d.open)));
  if (!ok || s.every((d) => d.closed)) return '';
  const groups = [];
  for (const i of [1, 2, 3, 4, 5, 6, 0]) {
    const v = s[i].closed ? 'closed' : `${time12(s[i].open)}–${time12(s[i].close)}`;
    const last = groups[groups.length - 1];
    if (last && last.v === v) last.to = i; else groups.push({ from: i, to: i, v });
  }
  return groups.map((g) => `${DAY3[g.from]}${g.to !== g.from ? `–${DAY3[g.to]}` : ''} ${g.v}`).join(' · ');
}
export const shopHoursText = (profile) => String((profile && profile.hours) || '').trim() || hoursSummary(profile && profile.hoursStructured);

// ?? Email helper ???????????????????????????????????????????????????????????????
export function buildEmailAction(email, customerName, bikeName, shopProfile = {}) {
  const firstName = customerName.split(' ')[0];
  const shop = {
    name: shopProfile.name || 'Your Shop',
    address: shopProfile.address || '',
    phone: shopProfile.phone || '',
    email: shopProfile.email || '',
    website: shopProfile.website || '',
    hours: shopHoursText(shopProfile),
    signature: shopProfile.signature || 'The Service Team',
    abandonedDays: shopProfile.abandonedDays || 30,
  };

  const lines = [];
  lines.push(`Hi ${firstName},`);
  lines.push('');
  lines.push(`Great news! Your ${bikeName} is ready for pickup at ${shop.name}.`);
  lines.push('');
  if (shop.address) lines.push(`📍 ${shop.address}`);
  if (shop.phone)   lines.push(`📞 ${shop.phone}`);
  if (shop.email)   lines.push(`✉ ${shop.email}`);
  if (shop.hours)   lines.push(`🕐 ${shop.hours}`);
  if (shop.website) lines.push(`🌐 ${shop.website}`);
  if (shop.address || shop.phone || shop.email || shop.hours || shop.website) lines.push('');
  lines.push('Please stop by at your earliest convenience.');
  lines.push('');
  lines.push(`⚠ Please note: Bikes left unclaimed for more than ${shop.abandonedDays} days will be considered abandoned property.`);
  lines.push('');
  lines.push(`Thank you for choosing ${shop.name}!`);
  lines.push(shop.signature);

  const plainBody = lines.join('\n');
  const subject = encodeURIComponent('Your bike is ready for pickup!');
  const body = encodeURIComponent(plainBody);

  return {
    gmail:   `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(email)}&su=${subject}&body=${body}`,
    outlook: `https://outlook.live.com/mail/0/deeplink/compose?to=${encodeURIComponent(email)}&subject=${subject}&body=${body}`,
    mailto:  `mailto:${email}?subject=${subject}&body=${body}`,
    plain:   plainBody,
  };
}

// ?? Clipboard helper ???????????????????????????????????????????????????????????
// Auth header for requests to requireAuth-gated routes.
//
// Reads the token at CALL time, not at render — the pattern this replaces
// (UserManagement.jsx captured it once at component scope) goes stale if the
// token changes mid-session.
//
// Returns an empty object when there's no token, so a call from a logged-out
// state produces a clean 401 from the server rather than sending "Bearer null".
// Calendar days between a timestamp and now, in the viewer's own timezone.
//
// NOT elapsed-time / 86400000, which is what this replaced. That treats
// anything under 24 hours as "Today", so a text sent at 6pm yesterday still
// read "Today" at 10am the next morning — the contact badge claimed a customer
// had been contacted today when nobody had. Staff use that badge to decide who
// to chase, so being a day out is worse than showing nothing.
export function calendarDaysAgo(ts) {
  if (!ts) return 0;
  const then = new Date(ts);
  if (Number.isNaN(then.getTime())) return 0;
  const now = new Date();
  const thenMidnight = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((nowMidnight - thenMidnight) / 86400000));
}

export function authHeaders(json = false) {
  const token = localStorage.getItem('sb_token');
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// -- Unauthorized handling ----------------------------------------------------
// A 401 from a staff route used to go nowhere. Most callers do
// `.then(r => r.json())` with no `r.ok` check, so the error body lands where a
// list was expected, the component stores an empty array, and the screen shows
// "No conversations" or an empty table. Staff see a working app with no data in
// it and no reason given.
//
// There are 167 fetch call sites. Adding a check to each one fixes today's and
// misses tomorrow's - which is exactly how this gap appeared. So the handling
// goes in one place: window.fetch is wrapped once, and every call site inherits
// it, including the ones not written yet.
//
// This matters more since MULTI-TENANCY.md phase 1, because requireAuth now
// fails closed on shop resolution - a database blip returns 401 on staff routes
// where it previously would have served them. That was the right trade, and it
// made this bug easier to reach.
//
// Deliberately narrow. It fires only when ALL of these hold:
//   - the response status is 401
//   - the request went to our own API origin
//   - it was not one of the public auth endpoints, where a 401 means "wrong
//     password", not "your session is gone"
//   - a token is actually present, so the public booking and payment pages,
//     which fetch without one, are never affected
//   - the request itself CARRIED that token (25 Oct). Signing out and straight
//     back in raced: the app re-checks /api/auth-status when the user changes,
//     a check sent while signed out (no token) came back 401 just after the new
//     token was saved, and the "couldn't verify your account" dialog appeared
//     over a perfectly good session (production, 14:01 UTC: the 401 had no
//     CORS preflight, i.e. no Authorization header). A 401 to a request made
//     with no token, or with an older one, says nothing about this session.
//
// The response is passed through untouched - the body is not read here, so a
// caller that wants to inspect it still can.

const PUBLIC_AUTH_PATHS = [
  '/auth/users/login',
  '/auth/users/forgot-password',
  '/auth/users/reset-password',
];

let _sessionProblemHandler = null;
let _fetchWrapped = false;

// Fires for 401 AND 503. The handler receives the status so it can tell the two
// apart, because they mean different things and deserve different words:
//
//   401 - this account is gone, or the token is finished. Sign in again.
//   503 - Spinboard cannot reach its database. Nothing is wrong with the
//         account; come back in a moment.
//
// The server only started distinguishing these on Aug 21. Before that a
// database blip answered 401 on every gated route at once, and staff read it as
// being logged out mid-shift. Handling 503 here is the half that makes the
// server-side change worth anything — without it a 503 lands in
// `.then(r => r.json())` and renders an empty screen, which is worse than the
// mass logout it replaced.
// The bearer token a request was sent with, or null.
function sentToken(input, init) {
  try {
    const h = (init && init.headers) || (input && typeof input !== 'string' && input.headers) || null;
    if (!h) return null;
    const v = typeof h.get === 'function' ? h.get('Authorization') : (h.Authorization || h.authorization);
    const m = /^Bearer\s+(.+)$/.exec(String(v || ''));
    return m ? m[1] : null;
  } catch (_) { return null; }
}

export function onSessionProblem(handler) {
  _sessionProblemHandler = handler;
  if (_fetchWrapped) return;
  _fetchWrapped = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await nativeFetch(input, init);
    try {
      if ((response.status === 401 || response.status === 503) && _sessionProblemHandler) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const ours = url.startsWith(API);
        const publicAuth = PUBLIC_AUTH_PATHS.some(p => url.includes(p));
        const token = localStorage.getItem('sb_token');
        if (ours && !publicAuth && token && sentToken(input, init) === token) {
          _sessionProblemHandler(response.status, url);
        }
      }
    } catch (_) {
      // Never let the interceptor break the request it is observing.
    }
    return response;
  };
}

export function copyToClipboard(text) {
  navigator.clipboard.writeText(text).catch(() => {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  });
}

// ?? Format helpers ?????????????????????????????????????????????????????????????
export function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
}

export function fmtTime(dt) {
  if (!dt) return '';
  return dt.toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' });
}

export function fmtCurrency(n) {
  return `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 })}`;
}

export function fmtPhone(p) {
  if (!p) return '';
  const d = p.replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  if (d.length === 11) return `+${d[0]} (${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
  return p;
}

export function daysAgoLabel(days) {
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}

export function urgencyColor(days) {
  if (days >= 30) return '#e74c3c';
  if (days >= 14) return '#e67e22';
  if (days >= 7)  return '#f1c40f';
  return '#2ecc71';
}

export const TECH_COLORS = ['#3498db','#9b59b6','#1abc9c','#e67e22','#555','#e74c3c'];
export const AGE_BUCKETS = [
  { label:'Today',    min:0, max:0,   color:'#2ecc71' },
  { label:'1–2 days', min:1, max:2,   color:'#3498db' },
  { label:'3–4 days', min:3, max:4,   color:'#e67e22' },
  { label:'5+ days',  min:5, max:999, color:'#e74c3c' },
];
// Shared SMS template defaults — used as a fallback in both Settings.jsx (for
// editing) and PickupWizard.jsx (for quick-send template buttons) whenever a
// shop hasn't saved custom template text. Single source of truth so the two
// don't drift out of sync. Generic across shops — {shopName}, {shopPhone},
// and {shopHours} are substituted from that shop's own settings.shopProfile.
export const DEFAULT_SMS_TEMPLATES = {
  pickup: "Hi {name}! Your {bike} is ready for pickup at {shopName}. Balance due at pickup: {balance}. Hours: {shopHours}. Questions? Call {shopPhone}.",
  pickup_reminder: "Hi {name}, a friendly reminder that your {bike} is still ready for pickup at {shopName}. Balance due: {balance}. Hours: {shopHours}. Questions? Call {shopPhone}.",
  estimate: "Hi {name}, we've taken a look at your {bike} and have an estimate ready for the recommended service. Please reply or call {shopPhone} to review and approve before we proceed. — {shopName}",
  special_order: "Hi {name}, good news — the special-order part for your {bike} has arrived at {shopName}. We'll get you scheduled. Reply or call {shopPhone} to set up a time.",
};

// Starts the Lightspeed OAuth flow.
//
// GET /auth/login is a browser redirect and so cannot carry a Bearer header,
// which is why it used to be reachable by anyone who knew the URL. It now
// requires a single-use handoff token, minted here by an authenticated call.
// Never navigate straight to /auth/login — it will refuse.
//
// allowAccountChange is owner-only and lets a shop move to a DIFFERENT
// Lightspeed account without first disconnecting, so there is never a window
// where the shop has no integration at all.
// Starts the Stripe Connect flow. Same shape as startLightspeedConnect below,
// and for the same reason: a plain navigation to the API carries no bearer
// token, so the server could not tell which shop was connecting. The URL is
// requested with credentials first; the state inside it is bound to this shop.
export async function startStripeConnect() {
  const r = await fetch(`${API}/api/stripe-connect/authorize-url`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(true),
    body: '{}',
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body.error || 'Could not start the Stripe connection.');
  }
  const { url } = await r.json().catch(() => ({}));
  if (!url || typeof url !== 'string') {
    console.error('[stripe] authorize-url returned no usable url');
    throw new Error('Spinboard could not build the Stripe connection link. Please try again.');
  }
  window.location.href = url;
}

export async function startLightspeedConnect(allowAccountChange = false) {
  const r = await fetch(`${API}/auth/login-url`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(true),
    body: JSON.stringify({ allowAccountChange }),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body.error || 'Could not start the Lightspeed connection.');
  }
  const { url } = await r.json().catch(() => ({}));
  // Guard the assignment. `window.location.href = undefined` does not throw and
  // does not stay put — it navigates to "/undefined", which the SPA's catch-all
  // route serves as index.html. The app re-renders and the whole thing looks
  // like the button did nothing. Fail loudly instead.
  if (!url || typeof url !== 'string') {
    console.error('[connect] login-url returned no usable url');
    throw new Error('Spinboard could not build the Lightspeed connection link. Please try again.');
  }
  console.log('[connect] navigating to', url);
  window.location.href = url;
}

// ── What a person reads for each role ────────────────────────────────────────
//
// The `owner` role displays as "Administrator" everywhere a human sees it. The
// stored value stays `owner`: it gates the Lightspeed and Stripe disconnect
// routes, the Users group and the onboarding console, so renaming the value
// would be a database migration for a word nobody reads.
//
// "Administrator" rather than "Admin" deliberately -- /admin is Spinboard's own
// platform console, and one word for two things is how a support conversation
// goes wrong. The reason for the rename (17 Sep): an owner who delegates setup
// to a manager reads "Owner" as "the owner has to do this".
//
// It lives in helpers rather than in App.js because UserManagement needs it too
// and App.js already imports that component -- the other direction would be a
// circular import.
export const ROLE_LABELS = { owner: 'Administrator', manager: 'Manager', tech: 'Tech', associate: 'Associate' };
export const roleLabel = (role) => ROLE_LABELS[role] || role || '';
